import { User } from 'typings/common_defs'
import { api } from 'api'
import { path } from 'ramda'
import { PermissionEnum } from '../userPermissions'
import { z } from 'zod'

export type CompanyMember = Pick<
  User,
  'id' | 'name' | 'email' | 'phoneNumber' | 'companyRolePermission'
>

export async function getCompanyMembers() {
  const data = await api.post(
    '/company/graphql_dispatch/current_company_c17690c4',
    {}
  )

  const members = path(
    ['data', 'data', 'company', 'members'],
    data
  ) as CompanyMember[]
  return members.map((member) => ({
    ...member,
    id: Number(member.id),
  }))
}

export type CompanyPendingInvite = Omit<CompanyMember, 'name' | 'phoneNumber'>

export async function getCompanyPendingInvites() {
  const data = await api.post(
    '/company/graphql_dispatch/current_company_192f0e05',
    {}
  )

  const pendingInvites = path(
    ['data', 'data', 'company', 'pendingInvites'],
    data
  ) as CompanyPendingInvite[]
  return pendingInvites.map((member) => ({
    ...member,
    id: Number(member.id),
  }))
}

export type CompanyRole = {
  id: number
  name?: string
  description?: string
} & {
  [key in PermissionEnum]: boolean
}

export async function getCompanyRoles() {
  const data = await api.post(
    '/company/graphql_dispatch/company_roles_12c52728',
    {}
  )

  const roles = path(
    ['data', 'data', 'company', 'rolePermissions'],
    data
  ) as CompanyRole[]
  return roles.map((role) => ({
    ...role,
    id: Number(role.id),
  }))
}

const allCompaniesMembersResponse = z.object({
  members: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      email: z.string(),
      companyRolePermission: z.object({
        id: z.number(),
        description: z.string(),
      }),
      companies: z.array(
        z.object({
          id: z.number(),
          name: z.string(),
        })
      ),
    })
  ),
  nextCursor: z.string().nullable(),
})

export type AllCompaniesMembersResponse = z.infer<
  typeof allCompaniesMembersResponse
>

export async function getAllCompaniesMembers(
  companyId: number,
  searchTerm?: string,
  cursor?: string,
  limit?: number
): Promise<AllCompaniesMembersResponse> {
  const response = await api.get(`/company/${companyId}/members`, {
    params: {
      searchTerm,
      cursor,
      limit,
    },
  })

  return allCompaniesMembersResponse.parse(response.data)
}

export async function inviteCompanyMember(
  companyId: number,
  email: string,
  rolePermissionId: number
) {
  return api.post(`/company/${companyId}/invite`, {
    email,
    rolePermissionId,
  })
}

export const updateCompanyGroupMemberCompaniesRequest = z.object({
  userId: z.number(),
  addCompanyIds: z.array(z.number()),
  removeCompanyIds: z.array(z.number()),
})

export type EditCompanyGroupMemberCompaniesRequest = z.infer<
  typeof updateCompanyGroupMemberCompaniesRequest
>

export async function updateCompanyGroupMemberCompanies(
  companyId: number,
  request: EditCompanyGroupMemberCompaniesRequest
) {
  return api.post(`/company/${companyId}/members`, request)
}
